import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Layers, Clock, MessageSquare, Play, Save, CheckCircle2, Activity, TrendingUp, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_LABELS } from "@/lib/workoutTaxonomy";
import { platesPerSide, formatPlateBreakdown } from "@/lib/plateMath";
import { videoEmbedUrl } from "@/lib/videoEmbed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AthleteWorkoutView — speed-first redesign (2026-05-28).
 *
 * Key UX change: max-weight inputs live INLINE next to each exercise name
 * within the workout. The "% Max" column shows the COMPUTED weight in
 * real time as the athlete edits their max. No more separate top-of-page
 * Maxes editor — everything's where the eye already is.
 *
 * Sibling isolation: every load + save carries the active athlete_id from
 * AuthContext, so each kid's maxes stay distinct.
 */
export default function AthleteWorkoutView() {
    const { user, loading, activeAthleteId } = useAuth();
    const { workoutId } = useParams();
    const navigate = useNavigate();
    const [workout, setWorkout] = useState(null);
    const [exercises, setExercises] = useState([]);
    const [maxesByExercise, setMaxesByExercise] = useState({});
    const [bio, setBio] = useState({ weight_lbs: null, height_in: null });
    const [equipmentById, setEquipmentById] = useState({});
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        if (!loading && !user) navigate("/", { replace: true });
    }, [loading, user, navigate]);

    useEffect(() => {
        (async () => {
            setBusy(true);
            try {
                const params = activeAthleteId ? { athlete_id: activeAthleteId } : {};
                // Core load: workout doc + exercise catalog. If these fail we
                // genuinely can't render the page (no blocks to show, no
                // exercise names to label sets with), so we surface the error.
                const [w, ex] = await Promise.all([
                    axios.get(`${API}/workouts/${workoutId}`, { params, withCredentials: true }),
                    axios.get(`${API}/exercises`, { withCredentials: true }),
                ]);
                setWorkout(w.data);
                setExercises(ex.data || []);

                // Stretch loads: per-athlete maxes / biometrics / equipment.
                // These can legitimately 400/403 for accounts with no
                // athlete profile (comp/demo, owner playing in athlete UI,
                // unfinished signup). Previously the whole Promise.all
                // rejected and the catch below bounced the user to
                // /dashboard/workouts, which rendered TrainingCategories,
                // producing the speed → list → 5S's loop Sachin reported
                // (2026-02-27). Soft-fail each independently so the
                // workout content always renders. (Sachin bug 2026-02-27.)
                const [mxRes, bRes, eqRes] = await Promise.all([
                    axios.get(`${API}/me/workout-maxes`, { params, withCredentials: true })
                        .catch(() => ({ data: { maxes: [] } })),
                    axios.get(`${API}/me/biometrics`, { params, withCredentials: true })
                        .catch(() => ({ data: {} })),
                    axios.get(`${API}/equipment`, { withCredentials: true })
                        .catch(() => ({ data: [] })),
                ]);
                setBio({ weight_lbs: bRes.data?.weight_lbs ?? null, height_in: bRes.data?.height_in ?? null });
                const eMap = {};
                for (const row of (eqRes.data || [])) eMap[row.equipment_id] = row;
                setEquipmentById(eMap);
                const m = {};
                for (const row of mxRes.data?.maxes || []) {
                    const prev = m[row.exercise_id] || 0;
                    if ((row.max_weight || 0) > prev) m[row.exercise_id] = row.max_weight || 0;
                }
                setMaxesByExercise(m);
            } catch (e) {
                toast.error("Couldn't load workout", { description: e?.response?.data?.detail });
                // Send the user back to wherever they came from (usually the
                // filtered list /dashboard/workouts?category=X) instead of
                // hard-replacing to /dashboard/workouts, which renders the
                // 5 S's category picker again and produced an infinite loop.
                // Falls back to /dashboard if there's no history.
                if (window.history.length > 1) navigate(-1);
                else navigate("/dashboard", { replace: true });
            } finally {
                setBusy(false);
            }
        })();
    }, [workoutId, activeAthleteId, navigate]);

    const exerciseMap = useMemo(
        () => Object.fromEntries((exercises || []).map((ex) => [ex.exercise_id, ex])),
        [exercises],
    );

    const updateMaxLocal = (exercise_id, value) => {
        setMaxesByExercise((m) => ({ ...m, [exercise_id]: value }));
    };

    if (loading || !user || busy || !workout) {
        return <div className="min-h-screen bg-brand-obsidian flex items-center justify-center"><Loader2 className="w-7 h-7 text-brand-red animate-spin" /></div>;
    }

    return (
        <div data-testid="athlete-workout-view" className="bg-brand-obsidian min-h-screen">
            <section className="pt-32 pb-8 border-b border-zinc-900">
                <div className="max-w-4xl mx-auto px-6 md:px-10">
                    <Link
                        to={`/dashboard/workouts${workout.category ? `?category=${encodeURIComponent(workout.category)}` : ""}`}
                        className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5"
                    >
                        <ArrowLeft className="w-3 h-3" /> {workout.category ? CATEGORY_LABELS[workout.category] : "Workouts"}
                    </Link>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" /> {CATEGORY_LABELS[workout.category]}
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl tracking-wider leading-[0.92] text-white">
                        {workout.title}
                    </h1>
                    <div className="mt-4 text-xs text-zinc-200 flex items-center gap-4 flex-wrap">
                        <span className="inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-brand-red" /> {workout.blocks.length} block{workout.blocks.length === 1 ? "" : "s"}</span>
                        {workout.estimated_duration && <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-brand-red" /> {workout.estimated_duration} min</span>}
                        {workout.subtabs?.length > 0 && <span className="text-zinc-300">{workout.subtabs.join(" · ")}</span>}
                    </div>
                    <div className="mt-6">
                        <Link
                            to={`/dashboard/workouts/${workoutId}/run`}
                            data-testid="start-workout-cta"
                            className="btn-tactical inline-flex"
                        >
                            <Play className="w-4 h-4" /> Start workout
                        </Link>
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-4xl mx-auto px-6 md:px-10 space-y-5">
                    <BiometricsCard
                        bio={bio}
                        onChange={setBio}
                        activeAthleteId={activeAthleteId}
                    />
                    {workout.blocks.map((b, bi) => (
                        <BlockCard
                            key={b.id || bi}
                            block={b}
                            blockIndex={bi}
                            exerciseMap={exerciseMap}
                            maxesByExercise={maxesByExercise}
                            onMaxLocalChange={updateMaxLocal}
                            activeAthleteId={activeAthleteId}
                            bodyweight={bio.weight_lbs}
                            equipmentById={equipmentById}
                        />
                    ))}
                    <div className="text-xs text-zinc-300 text-center pt-4">
                        Tap <span className="text-brand-red">Start workout</span> to enter Focus Mode — one set at a time, plates and rest timer included.
                    </div>
                </div>
            </section>
        </div>
    );
}

/**
 * BlockCard — one block of a workout. Levels render as horizontal tabs;
 * only the active level's exercise (single max input + sets table) shows.
 * Keeps the page short — athletes see only the level they're actually doing.
 */
function BlockCard({ block, blockIndex, exerciseMap, maxesByExercise, onMaxLocalChange, activeAthleteId, bodyweight, equipmentById }) {
    const [activeLevelIdx, setActiveLevelIdx] = useState(0);
    const safeIdx = Math.min(activeLevelIdx, block.levels.length - 1);
    const activeLevel = block.levels[safeIdx];

    return (
        <div data-testid={`athlete-block-${blockIndex}`} className="border border-zinc-900 bg-zinc-950">
            <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <span className="font-display text-3xl tracking-wider text-brand-red">{block.name}</span>
                    {block.link_type && (
                        <span className="text-[10px] tracking-[0.25em] uppercase border border-zinc-800 text-zinc-200 px-2 py-1">
                            {block.link_type}
                        </span>
                    )}
                </div>
                {block.levels.length > 1 && (
                    <div
                        role="tablist"
                        data-testid={`block-${blockIndex}-level-tabs`}
                        className="inline-flex border border-zinc-800 overflow-hidden"
                    >
                        {block.levels.map((lv, idx) => {
                            const active = idx === safeIdx;
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    data-testid={`block-${blockIndex}-level-tab-${idx}`}
                                    onClick={() => setActiveLevelIdx(idx)}
                                    className={`px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase transition-colors ${
                                        active
                                            ? "bg-brand-red text-white"
                                            : "bg-transparent text-zinc-300 hover:text-white"
                                    }`}
                                >
                                    L{lv.level ?? idx + 1}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            {block.coach_note && (
                <div className="px-5 py-3 border-b border-zinc-900 bg-black/40 flex items-start gap-2">
                    <MessageSquare className="w-3.5 h-3.5 text-brand-red flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-zinc-300 leading-relaxed">{block.coach_note}</p>
                </div>
            )}
            {activeLevel && (
                <LevelRow
                    level={activeLevel}
                    levelIndex={safeIdx}
                    blockLevelCount={block.levels.length}
                    exercise={exerciseMap[activeLevel.exercise_id]}
                    currentMax={maxesByExercise[activeLevel.exercise_id] || 0}
                    onMaxLocalChange={(v) => onMaxLocalChange(activeLevel.exercise_id, v)}
                    activeAthleteId={activeAthleteId}
                    bodyweight={bodyweight}
                    equipmentById={equipmentById}
                />
            )}
        </div>
    );
}

/**
 * Single level row — exercise name + inline max input + sets table that
 * dynamically shows computed weights in the % column based on the max.
 *
 * The max input auto-saves with a 600ms debounce so the athlete doesn't
 * have to hit Save. Visual save-state indicator on the input border.
 */
function LevelRow({ level, levelIndex, blockLevelCount, exercise, currentMax, onMaxLocalChange, activeAthleteId, bodyweight, equipmentById }) {
    const [maxStr, setMaxStr] = useState(currentMax ? String(currentMax) : "");
    const [saveState, setSaveState] = useState("idle");  // idle | saving | saved | error
    const debounceRef = useRef(null);

    // Sync external changes (e.g. another LevelRow editing the same exercise)
    useEffect(() => {
        const next = currentMax ? String(currentMax) : "";
        if (next !== maxStr) setMaxStr(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMax]);

    const maxNum = parseFloat(maxStr) || 0;
    const exerciseName = level.exercise_name || exercise?.exercise_name || "";
    // Derive the percentage's natural max_type from exercise config — defaults
    // to weight_lbs / 1RM. Workouts that use a different config (% of best
    // speed, etc.) can extend later; today everything is weight-based.
    const maxType = exercise?.maxes_config?.[0]?.label || "1RM";

    const onChange = (e) => {
        const v = e.target.value;
        setMaxStr(v);
        const num = parseFloat(v) || 0;
        onMaxLocalChange(num);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        // Don't try to save garbage / negatives; the backend ignores ≤0 anyway
        // but we save bandwidth by not even POSTing.
        if (num <= 0) { setSaveState("idle"); return; }
        setSaveState("saving");
        debounceRef.current = setTimeout(async () => {
            try {
                await axios.post(
                    `${API}/me/workout-maxes`,
                    {
                        exercise_id: level.exercise_id,
                        exercise_name: exerciseName,
                        max_weight: num,
                        max_type: maxType,
                    },
                    {
                        params: activeAthleteId ? { athlete_id: activeAthleteId } : {},
                        withCredentials: true,
                    },
                );
                setSaveState("saved");
                setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1500);
            } catch (err) {
                setSaveState("error");
                toast.error("Couldn't save max", { description: err?.response?.data?.detail });
            }
        }, 600);
    };

    // Compute the displayed weight for a given % — rounded to the nearest 5 lb
    // (matches the plate granularity in Focus Mode + most strength programs).
    const weightFor = (pct) => {
        if (maxNum <= 0) return null;
        const raw = (Number(pct) / 100) * maxNum;
        return Math.round(raw / 5) * 5;
    };

    const hasNextLevel = blockLevelCount > (levelIndex + 1);
    const nextLevelHint = hasNextLevel
        ? <div className="text-[9px] tracking-[0.18em] uppercase text-zinc-500 mt-0.5">Hit it → ready for L{levelIndex + 2}</div>
        : null;

    const goalTxt = (() => {
        if (level.advancement_type && level.advancement_value != null) {
            const labels = {
                bw_multiplier: `Goal · ${level.advancement_value}× BW`,
                absolute: `Goal · ${level.advancement_value} lb`,
                reps: `Goal · ${level.advancement_value} reps`,
            };
            return labels[level.advancement_type] || null;
        }
        if (level.bw_multiplier != null) return `Goal · ${level.bw_multiplier}× BW`;
        return null;
    })();

    // Compute "ready to advance" — true when the athlete's current max meets
    // or exceeds the level's goal. Only meaningful for weight-based goals
    // (bw_multiplier / absolute). Reps-based goals require in-workout judgment.
    const goalWeight = (() => {
        if (level.advancement_type === "absolute" && level.advancement_value != null) {
            return Number(level.advancement_value);
        }
        const mult = level.advancement_type === "bw_multiplier"
            ? Number(level.advancement_value)
            : (level.bw_multiplier != null ? Number(level.bw_multiplier) : null);
        if (mult != null && bodyweight && bodyweight > 0) return mult * bodyweight;
        return null;
    })();
    const goalMet = goalWeight != null && maxNum > 0 && maxNum >= goalWeight;

    const stateBorder =
        saveState === "saving" ? "border-amber-500/60" :
        saveState === "saved" ? "border-emerald-500/60" :
        saveState === "error" ? "border-red-500/60" :
        maxNum > 0 ? "border-zinc-700" : "border-zinc-800";

    // Demo video — coach-attached on the exercise (single URL field today,
    // bulk-assignable via Coach → Exercise Library → "Apply video to selected").
    // We keep the player collapsed by default so the workout page stays
    // scannable; tapping the button injects the iframe lazily.
    const demoEmbed = videoEmbedUrl(exercise?.video_url);
    const demoRaw = exercise?.video_url || "";
    const [demoOpen, setDemoOpen] = useState(false);

    return (
        <div className="border-t border-zinc-900 first:border-t-0 px-5 py-4">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Level {level.level}</div>
                        <div className="font-display text-2xl tracking-wider text-white leading-tight">{exerciseName}</div>
                    </div>
                    {/* Inline max input next to the exercise name */}
                    <div className="flex items-center gap-1.5">
                        <label
                            htmlFor={`max-${level.exercise_id}-${levelIndex}`}
                            className="text-[10px] tracking-[0.25em] uppercase text-zinc-300"
                        >
                            Max
                        </label>
                        <div className="relative">
                            <input
                                id={`max-${level.exercise_id}-${levelIndex}`}
                                data-testid={`inline-max-${level.exercise_id}`}
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="5"
                                value={maxStr}
                                onChange={onChange}
                                placeholder="—"
                                className={`w-20 bg-black border ${stateBorder} px-2 py-1.5 text-sm text-white text-center font-mono focus:outline-none focus:border-brand-red transition-colors`}
                            />
                            {saveState === "saving" && (
                                <Loader2 className="w-3 h-3 text-amber-400 animate-spin absolute -right-5 top-1/2 -translate-y-1/2" />
                            )}
                            {saveState === "saved" && (
                                <CheckCircle2 className="w-3 h-3 text-emerald-400 absolute -right-5 top-1/2 -translate-y-1/2" />
                            )}
                            {saveState === "error" && (
                                <Save className="w-3 h-3 text-red-400 absolute -right-5 top-1/2 -translate-y-1/2" />
                            )}
                        </div>
                        <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">lb</span>
                    </div>
                </div>
                {goalTxt && (
                    <div className="text-right">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">{goalTxt}</div>
                        {goalMet ? (
                            <div
                                data-testid={`ready-to-advance-${level.exercise_id}`}
                                className="text-[10px] tracking-[0.2em] uppercase text-emerald-400 mt-0.5 inline-flex items-center gap-1"
                            >
                                <TrendingUp className="w-3 h-3" /> Ready to advance
                            </div>
                        ) : nextLevelHint}
                    </div>
                )}
            </div>

            {/* Demo video disclosure — collapsed by default. Coaches attach
                ONE URL per exercise (single-URL bulk-fillable from the
                Exercise Library); athletes get a tap-to-expand player so
                the page stays scannable. */}
            {(demoEmbed || demoRaw) && (
                <div className="mb-3">
                    <button
                        type="button"
                        data-testid={`watch-demo-toggle-${level.exercise_id}`}
                        onClick={() => setDemoOpen((v) => !v)}
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase border border-zinc-800 hover:border-brand-red text-zinc-300 hover:text-white px-2.5 py-1.5 transition-colors"
                    >
                        <Play className="w-3 h-3 text-brand-red" />
                        {demoOpen ? "Hide demo" : "Watch demo"}
                        <ChevronDown className={`w-3 h-3 transition-transform ${demoOpen ? "rotate-180" : ""}`} />
                    </button>
                    {demoOpen && (
                        <div
                            data-testid={`watch-demo-panel-${level.exercise_id}`}
                            className="mt-2 border border-zinc-800 bg-black rounded overflow-hidden"
                        >
                            {demoEmbed ? (
                                <div className="relative" style={{ paddingTop: "56.25%" }}>
                                    <iframe
                                        title={`Demo: ${exerciseName}`}
                                        src={demoEmbed}
                                        loading="lazy"
                                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        className="absolute inset-0 w-full h-full"
                                    />
                                </div>
                            ) : (
                                <a
                                    href={demoRaw}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block px-3 py-2 text-xs text-brand-red hover:underline"
                                >
                                    Open demo in new tab → {demoRaw}
                                </a>
                            )}
                        </div>
                    )}
                </div>
            )}
            {/* Sets table — % column dynamically renders computed weight,
                plus a "plates each side" column for barbell exercises. */}
            {(() => {
                const barEq = exercise?.bar_equipment_id ? equipmentById?.[exercise.bar_equipment_id] : null;
                const barWeight = barEq?.weight;
                const platesAvail = barEq?.plates_available || [];
                const showPlates = barEq && barWeight != null && barWeight > 0 && platesAvail.length > 0;
                return (
                    <div className="border border-zinc-900">
                        <div className="grid grid-cols-12 bg-black border-b border-zinc-900 text-[10px] tracking-[0.2em] uppercase text-zinc-300">
                            <div className="col-span-2 px-2 py-2 text-center">Set</div>
                            <div className={`${showPlates ? "col-span-3" : "col-span-4"} px-2 py-2 text-center`}>{maxNum > 0 ? "Weight" : "% Max"}</div>
                            {showPlates && <div className="col-span-3 px-2 py-2 text-center">Plates / side</div>}
                            <div className={`${showPlates ? "col-span-2" : "col-span-3"} px-2 py-2 text-center`}>Reps</div>
                            <div className={`${showPlates ? "col-span-2" : "col-span-3"} px-2 py-2 text-center`}>Rest</div>
                        </div>
                        {level.sets_data.map((s, si) => {
                            const computed = weightFor(s.percent_max);
                            const plateResult = showPlates && computed != null
                                ? platesPerSide(computed, barWeight, platesAvail)
                                : null;
                            return (
                                <div key={si} className="grid grid-cols-12 border-b border-zinc-900 last:border-b-0 bg-zinc-950">
                                    <div className="col-span-2 px-2 py-2.5 text-center font-display text-xl text-brand-red">{si + 1}</div>
                                    <div
                                        data-testid={`computed-weight-${level.exercise_id}-${si}`}
                                        className={`${showPlates ? "col-span-3" : "col-span-4"} px-2 py-2.5 text-center text-sm text-white`}
                                    >
                                        {computed != null ? (
                                            <span className="font-mono">
                                                <span className="text-white">{computed}</span>
                                                <span className="text-zinc-500"> lb</span>
                                                <span className="text-zinc-500 text-[10px] ml-1.5">({s.percent_max}%)</span>
                                            </span>
                                        ) : (
                                            <span className="text-zinc-600">—</span>
                                        )}
                                    </div>
                                    {showPlates && (
                                        <div
                                            data-testid={`plates-per-side-${level.exercise_id}-${si}`}
                                            className="col-span-3 px-2 py-2.5 text-center text-[11px] text-zinc-200 font-mono leading-tight"
                                        >
                                            {plateResult ? formatPlateBreakdown(plateResult) : <span className="text-zinc-600">—</span>}
                                        </div>
                                    )}
                                    <div className={`${showPlates ? "col-span-2" : "col-span-3"} px-2 py-2.5 text-center text-sm text-white`}>{s.reps}</div>
                                    <div className={`${showPlates ? "col-span-2" : "col-span-3"} px-2 py-2.5 text-center text-sm text-zinc-200`}>{s.rest_mins} min</div>
                                </div>
                            );
                        })}
                    </div>
                );
            })()}
        </div>
    );
}


/**
 * BiometricsCard — compact height + weight inputs for the active athlete.
 * Auto-save with debounce (same pattern as the max inputs). The weight here
 * drives the "ready to advance" detection on bw_multiplier-style goals.
 */
function BiometricsCard({ bio, onChange, activeAthleteId }) {
    const [weight, setWeight] = useState(bio.weight_lbs != null ? String(bio.weight_lbs) : "");
    const [height, setHeight] = useState(bio.height_in != null ? String(bio.height_in) : "");
    const [saveState, setSaveState] = useState("idle");
    const debounceRef = useRef(null);

    useEffect(() => {
        setWeight(bio.weight_lbs != null ? String(bio.weight_lbs) : "");
        setHeight(bio.height_in != null ? String(bio.height_in) : "");
    }, [bio.weight_lbs, bio.height_in]);

    const persist = (nextWeight, nextHeight) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const w = parseFloat(nextWeight);
        const h = parseFloat(nextHeight);
        const payload = {};
        if (!Number.isNaN(w) && w > 0) payload.weight_lbs = w;
        if (!Number.isNaN(h) && h > 0) payload.height_in = h;
        if (Object.keys(payload).length === 0) { setSaveState("idle"); return; }
        setSaveState("saving");
        debounceRef.current = setTimeout(async () => {
            try {
                const r = await axios.put(
                    `${API}/me/biometrics`,
                    payload,
                    {
                        params: activeAthleteId ? { athlete_id: activeAthleteId } : {},
                        withCredentials: true,
                    },
                );
                onChange({
                    weight_lbs: r.data?.weight_lbs ?? null,
                    height_in: r.data?.height_in ?? null,
                });
                setSaveState("saved");
                setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1500);
            } catch (err) {
                setSaveState("error");
                toast.error("Couldn't save biometrics", { description: err?.response?.data?.detail });
            }
        }, 600);
    };

    const onWeight = (e) => { setWeight(e.target.value); persist(e.target.value, height); };
    const onHeight = (e) => { setHeight(e.target.value); persist(weight, e.target.value); };

    const stateColor =
        saveState === "saving" ? "text-amber-400" :
        saveState === "saved" ? "text-emerald-400" :
        saveState === "error" ? "text-red-400" : "text-zinc-500";

    return (
        <div
            data-testid="biometrics-card"
            className="border border-zinc-900 bg-zinc-950 px-5 py-4 flex items-center justify-between gap-4 flex-wrap"
        >
            <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-brand-red" />
                <span className="font-display text-xl tracking-wider text-white">Biometrics</span>
            </div>
            <div className="flex items-center gap-5 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-zinc-200">
                    <span className="tracking-[0.2em] uppercase text-[10px] text-zinc-300">Weight</span>
                    <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.5"
                        value={weight}
                        onChange={onWeight}
                        placeholder="—"
                        data-testid="biometrics-weight"
                        className="w-20 bg-black border border-zinc-800 px-2 py-1.5 text-sm text-white text-center font-mono focus:outline-none focus:border-brand-red"
                    />
                    <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">lb</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-zinc-200">
                    <span className="tracking-[0.2em] uppercase text-[10px] text-zinc-300">Height</span>
                    <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.25"
                        value={height}
                        onChange={onHeight}
                        placeholder="—"
                        data-testid="biometrics-height"
                        className="w-20 bg-black border border-zinc-800 px-2 py-1.5 text-sm text-white text-center font-mono focus:outline-none focus:border-brand-red"
                    />
                    <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">in</span>
                </label>
                <span data-testid="biometrics-state" className={`text-[10px] tracking-[0.2em] uppercase ${stateColor}`}>
                    {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : saveState === "error" ? "Error" : ""}
                </span>
            </div>
        </div>
    );
}
